import { initializeApp } from "firebase/app";
import { initializeFirestore } from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getAnalytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  ...(process.env.REACT_APP_FIREBASE_MEASUREMENT_ID && {
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  }),
};

export const app = initializeApp(firebaseConfig);
export let analytics = null;
try {
  if (firebaseConfig.measurementId) {
    analytics = getAnalytics(app);
  }
} catch {}

export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  useFetchStreams: false,
});

export const auth = getAuth(app);

// One-time persistence setup so the same UID is reused across reloads/tabs.
// Falls back gracefully if local persistence isn’t available (e.g., strict/privacy modes).
const persistenceReady = (async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch {
    try {
      await setPersistence(auth, browserSessionPersistence);
    } catch {
      await setPersistence(auth, inMemoryPersistence);
    }
  }
})();

// Single promise to avoid race conditions
let authReadyPromise;

/** Resolves when a user exists and the ID token is fresh */
export function ensureAnonAuth() {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve, reject) => {
      const unsub = onAuthStateChanged(auth, async (u) => {
        try {
          // make sure persistence is in place BEFORE any sign-in
          await persistenceReady;
          if (!u) {
            const cred = await signInAnonymously(auth);
            u = cred.user;
          }
          // force a fresh token so callables attach it
          await u.getIdToken(true);
          unsub();
          resolve(u);
        } catch (e) {
          unsub();
          reject(e);
        }
      });
    });
  }
  return authReadyPromise;
}
